import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Mail } from 'lucide-react';
import FloatingSymbols from '../components/FloatingSymbols';
import BrandLogo from '../components/BrandLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Forgot-password entry point. Deliberately shows the same "if the email
 * exists we've sent a link" copy whether the address is on file or not —
 * matches the backend's non-enumerating response, so this page can't be
 * used to discover which emails have accounts.
 */
const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setSubmitted(true);
    } catch (err) {
      // Even on server error, don't leak enumeration signal — show generic message.
      toast.error(err.response?.data?.detail || 'Could not send reset email — please retry in a moment');
    } finally { setLoading(false); }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4 overflow-hidden">
      <FloatingSymbols opacity={0.9} />
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-6">
          <BrandLogo size={44} className="mb-2 justify-center w-full" />
          <p className="text-slate-600">Reset your password</p>
        </div>

        <Card className="border-slate-200 shadow-lg" data-testid="forgot-password-card">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Mail className="h-5 w-5 text-blue-800" />
              Forgot Password
            </CardTitle>
            <CardDescription>
              Enter your account email — we'll send you a secure link to set a new password.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {submitted ? (
              <div data-testid="forgot-password-success" className="text-sm">
                <p className="text-slate-800 mb-3">
                  If an account exists for <strong>{email}</strong>, we've sent a password-reset link to that inbox.
                  The link stays valid for 1 hour.
                </p>
                <p className="text-slate-600 mb-4">
                  Don't see it in a few minutes? Check your spam folder, or double-check the email you used to sign up.
                </p>
                <Link to="/login" className="text-blue-800 hover:text-blue-900 font-medium" data-testid="forgot-password-back-to-login">
                  ← Back to sign in
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="fp-email">Email</Label>
                  <Input
                    id="fp-email"
                    type="email"
                    placeholder="agent@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="forgot-password-email-input"
                    autoComplete="email"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-blue-800 hover:bg-blue-900 text-white font-medium"
                  disabled={loading || !email}
                  data-testid="forgot-password-submit-button"
                >
                  {loading ? 'Sending reset link…' : 'Send reset link'}
                </Button>
                <div className="text-center text-sm">
                  <Link to="/login" className="text-slate-600 hover:text-blue-800" data-testid="forgot-password-cancel-link">
                    ← Back to sign in
                  </Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
